import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RECORD_DATA_TYPE } from '@demandproai/shared';
import {
  EASY_MODE_COMPLETED_STATUSES,
  EASY_MODE_UPLOAD_STATUS
} from '../constants/easyModeDocuments';
import { removeUpload } from '../redux/actions/fileUpload';
import { removeEasyModeRecord } from '../services/easyModeService';
import uploadManager from '../services/UploadManager';
import { errorToast, warnToast } from '../utils/toast';
import {
  applyRecordToRow,
  getFileNameFromS3UrlPath,
  getRecordForUniqueKey,
  getRecordJoinKey
} from '../utils/easyModeRecords';
import {
  computeFileMd5,
  getCandidateIdentity,
  toMetaDataSafeFolderPath,
  toUploadCandidates
} from '../utils/easyModeFileUtils';
import { buildUploadStateByUploadId } from '../utils/easyModeUploadState';
import useEasyModeRecords from './useEasyModeRecords';
import useUploadQueue from './useUploadQueue';

const UPLOADING_STATUSES = [
  EASY_MODE_UPLOAD_STATUS.PENDING,
  EASY_MODE_UPLOAD_STATUS.UPLOADING
];

// UPLOADED is in flight too: the bytes are in S3 but no record has come back yet, so the document has
// not settled and the wizard must not let the user move past it
const IN_FLIGHT_STATUSES = [
  ...UPLOADING_STATUSES,
  EASY_MODE_UPLOAD_STATUS.UPLOADED,
  EASY_MODE_UPLOAD_STATUS.CLASSIFYING
];

// the two labels in the mock cannot tell an upload from a classification, and the wizard now spends
// real time in each. Both hold Next now - an errored file has to surface before the run - so classifying
// no longer says "you can continue".
export const getHeaderLabel = ({ hasUploadInFlight, isWorkInFlight }) => {
  if (hasUploadInFlight) return 'Uploading documents...';

  return isWorkInFlight ? 'Classifying documents...' : 'Documents';
};

const TERMINAL_STATUSES = [EASY_MODE_UPLOAD_STATUS.UPLOADED, EASY_MODE_UPLOAD_STATUS.FAILED];

const buildRowId = () => `easy-mode-row-${Math.random().toString(36).slice(2, 11)}`;

const buildFolderMetaData = ({ folderPath }) => {
  const safeFolderPath = toMetaDataSafeFolderPath({ folderPath });

  return safeFolderPath ? { folderPath: safeFolderPath } : {};
};

const buildRow = ({ fileName, size = 0, status, recordType, uniqueKey = '', folderPath = '' }) => ({
  id: buildRowId(),
  uploadId: '',
  uniqueKey,
  fileName,
  folderPath,
  size,
  status: status ?? EASY_MODE_UPLOAD_STATUS.PENDING,
  recordType: recordType ?? RECORD_DATA_TYPE.UNCLASSIFIED
});

// a record is the only trace of a file that finished uploading in an earlier session, so it is what
// a refresh rebuilds the list from
const buildRowFromRecord = (record) =>
  buildRow({
    fileName: record.originalFileName ?? getFileNameFromS3UrlPath(record.s3UrlPath),
    status: EASY_MODE_UPLOAD_STATUS.UPLOADED,
    recordType: record.recordType,
    uniqueKey: record.s3UrlPath,
    folderPath: record.folderPath ?? ''
  });

const useEasyModeDocuments = ({ caseId }) => {
  const dispatch = useDispatch();
  const [storedRows, setStoredRows] = useState([]);
  // Seeding is deliberate rather than continuous: a list that reseeded itself whenever records
  // changed would resurrect a just-removed file in the gap before its soft-delete lands. The first
  // load is one generation, a partner import is the next.
  const [seedGeneration, setSeedGeneration] = useState(0);
  const seededGenerationRef = useRef(-1);
  // the File is held outside row state so nothing tries to serialise it, and so a row that never
  // reached a presigned URL still has something to retry with
  const filesByRowIdRef = useRef(new Map());
  const fileUpload = useSelector((state) => state.fileUpload);
  const { queueFiles } = useUploadQueue(caseId);

  const uploadStateByUploadId = useMemo(
    () => buildUploadStateByUploadId({ fileUpload }),
    [fileUpload]
  );

  // a row keeps its own status only while redux is not tracking it, which is what lets a reseeded
  // row show the state it was saved in
  const uploadedRows = useMemo(
    () =>
      storedRows.map((row) => {
        const uploadState = uploadStateByUploadId[row.uploadId];

        return uploadState ? { ...row, ...uploadState } : row;
      }),
    [storedRows, uploadStateByUploadId]
  );

  const awaitedS3UrlPaths = useMemo(
    () =>
      uploadedRows
        .filter((row) => row.uniqueKey && row.status === EASY_MODE_UPLOAD_STATUS.UPLOADED)
        .map((row) => row.uniqueKey),
    [uploadedRows]
  );

  const {
    recordByS3UrlPath,
    recordByJoinKey,
    recordCount,
    isRecordsLoading,
    refetchRecords,
    dropRecord
  } = useEasyModeRecords({ caseId, awaitedS3UrlPaths });

  const rows = useMemo(
    () =>
      uploadedRows.map((row) =>
        applyRecordToRow({
          row,
          record: getRecordForUniqueKey({ recordByJoinKey, uniqueKey: row.uniqueKey })
        })
      ),
    [uploadedRows, recordByJoinKey]
  );

  useEffect(() => {
    if (isRecordsLoading || seededGenerationRef.current === seedGeneration) return;

    seededGenerationRef.current = seedGeneration;
    setStoredRows((previous) => {
      // join keys rather than raw paths, so a row that uploaded a docx is not seeded a second time
      // under the pdf the PFQ converted it into
      const trackedKeys = new Set(
        previous.map((row) => row.uniqueKey).filter(Boolean).map(getRecordJoinKey)
      );
      const seededRows = Object.values(recordByS3UrlPath)
        .filter((record) => !trackedKeys.has(getRecordJoinKey(record.s3UrlPath)))
        .map(buildRowFromRecord);

      return [...seededRows, ...previous];
    });
  }, [isRecordsLoading, seedGeneration, recordByS3UrlPath]);

  // the widget's Clear drops completed uploads out of redux, so a settled row has to own its status
  // rather than keep deriving one that is about to disappear
  useEffect(() => {
    setStoredRows((previous) => {
      let hasChange = false;

      const next = previous.map((row) => {
        const liveStatus = uploadStateByUploadId[row.uploadId]?.status;

        if (!TERMINAL_STATUSES.includes(liveStatus) || liveStatus === row.status) return row;

        hasChange = true;

        return { ...row, status: liveStatus };
      });

      return hasChange ? next : previous;
    });
  }, [uploadStateByUploadId]);

  // a freshly uploaded file has a record within a moment, so ask for it rather than wait a poll out
  const awaitedKey = awaitedS3UrlPaths.join('|');

  useEffect(() => {
    const hasUnknownRecord = awaitedS3UrlPaths.some(
      (s3UrlPath) => !getRecordForUniqueKey({ recordByJoinKey, uniqueKey: s3UrlPath })
    );

    if (hasUnknownRecord) refetchRecords();
  }, [awaitedKey]);

  const setRowsStatus = ({ rowIds, status }) => {
    setStoredRows((previous) =>
      previous.map((row) => (rowIds.has(row.id) ? { ...row, status } : row))
    );
  };

  // rows render before their presigned URLs resolve, so the uploadId and the join key are patched
  // in afterwards
  const queueRows = async ({ rowIds }) => {
    const pendingUploads = rowIds
      .map((rowId) => ({ rowId, candidate: filesByRowIdRef.current.get(rowId) }))
      .filter(({ candidate }) => Boolean(candidate?.file));

    if (pendingUploads.length === 0) return;

    const pendingRowIds = new Set(pendingUploads.map(({ rowId }) => rowId));

    setRowsStatus({ rowIds: pendingRowIds, status: EASY_MODE_UPLOAD_STATUS.PENDING });

    try {
      // UNCLASSIFIED is what makes UploadManager dedicate the file, so a record exists to classify
      const { uploadTasks } = await queueFiles({
        files: pendingUploads.map(({ candidate }) => ({
          file: candidate.file,
          // the folder travels as S3 user metadata, the same channel a manual medical upload uses
          // for isPost, so it survives to the record the classifier reads
          metaData: buildFolderMetaData(candidate)
        })),
        caseRecordType: RECORD_DATA_TYPE.UNCLASSIFIED,
        hasInlineProgress: true
      });
      const uploadTaskByRowId = new Map(
        pendingUploads.map(({ rowId }, index) => [rowId, uploadTasks[index]])
      );

      setStoredRows((previous) =>
        previous.map((row) => {
          const uploadTask = uploadTaskByRowId.get(row.id);

          return uploadTask
            ? { ...row, uploadId: uploadTask.id, uniqueKey: uploadTask.uniqueKey }
            : row;
        })
      );
    } catch {
      // queueFiles already toasts; without a presigned URL these rows have no upload to watch
      setRowsStatus({ rowIds: pendingRowIds, status: EASY_MODE_UPLOAD_STATUS.FAILED });
    }
  };

  // Content dedup by md5: a file whose bytes already back a row is dropped, so re-adding the same
  // document, even under a different name, cannot upload it twice. Only rows we still hold the File for
  // can be compared - a row seeded from a record has no bytes on the client to hash.
  const dropDuplicateCandidates = async (candidates) => {
    const seenMd5s = new Set(
      [...filesByRowIdRef.current.values()].map((candidate) => candidate.md5).filter(Boolean)
    );
    const uniqueCandidates = [];
    let duplicateCount = 0;

    for (const candidate of candidates) {
      const md5 = await computeFileMd5(candidate.file);

      if (seenMd5s.has(md5)) {
        duplicateCount += 1;
        continue;
      }

      seenMd5s.add(md5);
      uniqueCandidates.push({ ...candidate, md5 });
    }

    if (duplicateCount > 0) {
      warnToast(`Skipped ${duplicateCount} duplicate file${duplicateCount > 1 ? 's' : ''}`);
    }

    return uniqueCandidates;
  };

  const addFiles = async ({ files }) => {
    if (files.length === 0) return;

    const candidates = await dropDuplicateCandidates(toUploadCandidates(files));

    if (candidates.length === 0) return;

    const newRows = candidates.map(({ file, folderPath }) =>
      buildRow({ fileName: file.name, size: file.size, folderPath })
    );

    newRows.forEach((row, index) => filesByRowIdRef.current.set(row.id, candidates[index]));
    setStoredRows((previous) => [...previous, ...newRows]);

    await queueRows({ rowIds: newRows.map((row) => row.id) });
  };

  // a partner import writes its documents straight onto the case, so there is no upload to watch -
  // the records are already there and the list only has to take them on
  const importRecordsAsRows = useCallback(async () => {
    await refetchRecords();
    setSeedGeneration((previous) => previous + 1);
  }, [refetchRecords]);

  const removeRow = async ({ id }) => {
    const row = rows.find((candidate) => candidate.id === id);

    if (row?.uploadId) {
      uploadManager.cancelUpload(row.uploadId);
      dispatch(removeUpload(row.uploadId));
    }

    filesByRowIdRef.current.delete(id);
    setStoredRows((previous) => previous.filter((candidate) => candidate.id !== id));

    const record = row && getRecordForUniqueKey({ recordByJoinKey, uniqueKey: row.uniqueKey });

    if (!record) return;

    dropRecord({ s3UrlPath: record.s3UrlPath });

    try {
      await removeEasyModeRecord({ s3UrlPath: record.s3UrlPath });
    } catch (error) {
      console.error('Error removing easy mode record:', error);
      errorToast('Failed to remove document. Please try again.');
    }
  };

  const retryRow = ({ id }) => {
    const row = rows.find((candidate) => candidate.id === id);

    if (!row) return;

    // no live upload means nothing to revive - the row never got a presigned URL, or redux has
    // already dropped it - so it starts over from the File we kept
    if (!uploadStateByUploadId[row.uploadId]) {
      queueRows({ rowIds: [id] });
      return;
    }

    uploadManager.retryUpload(row.uploadId);
  };

  const completedCount = rows.filter((row) =>
    EASY_MODE_COMPLETED_STATUSES.includes(row.status)
  ).length;

  const isWorkInFlight = rows.some((row) => IN_FLIGHT_STATUSES.includes(row.status));
  const hasUploadInFlight = rows.some((row) => UPLOADING_STATUSES.includes(row.status));

  return {
    rows,
    completedCount,
    totalCount: rows.length,
    recordCount,
    isRecordsLoading,
    isWorkInFlight,
    headerLabel: getHeaderLabel({ hasUploadInFlight, isWorkInFlight }),
    existingFileKeys: rows.map((row) =>
      getCandidateIdentity({ file: { name: row.fileName }, folderPath: row.folderPath })
    ),
    addFiles,
    importRecordsAsRows,
    removeRow,
    retryRow
  };
};

export default useEasyModeDocuments;
